import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { startMusic, stopMusic } from "@/lib/sounds";
import { Provider, useSelector, useDispatch } from "react-redux";
import { store } from "../store";
import * as actions from "../store/gameSlice";
import { api } from "@/lib/api";

const GameContext = createContext(null);

export function GameProvider({ children }) {
  return (
    <Provider store={store}>
      <GameInnerProvider>{children}</GameInnerProvider>
    </Provider>
  );
}

function GameInnerProvider({ children }) {
  const state = useSelector((state) => state.game);
  const dispatch = useDispatch();
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (loaded) {
      if (state.musicOn) {
        startMusic();
      } else {
        stopMusic();
      }
    }
  }, [state.musicOn, loaded]);

  const update = useCallback((patch) => {
    dispatch(actions.updateState(patch));
  }, [dispatch]);

  const syncAccess = useCallback(async (parentId) => {
    if (!parentId) return;
    try {
      const data = await api.getParentAccess(parentId);
      update({
        hasFullAccess: data.has_full_access,
        unlockedLayers: data.unlocked_layers || [],
        isInvestor: data.is_investor || false,
        investorSlots: data.investor_slots || 0
      });
    } catch (err) {
      console.warn("Failed to sync access data:", err);
      toast.error(
        state.language === "id"
          ? "Koneksi lambat/offline. Gagal menyinkronkan status akses premium."
          : "Slow connection/offline. Failed to sync premium access status."
      );
    }
  }, [update, state.language]);

  useEffect(() => {
    if (loaded && state.parentId) {
      syncAccess(state.parentId);
    }
  }, [loaded, state.parentId, syncAccess]);

  useEffect(() => {
    const isProd = process.env.REACT_APP_MIDTRANS_IS_PRODUCTION === "True" || process.env.REACT_APP_MIDTRANS_IS_PRODUCTION === "true";
    const src = isProd 
      ? "https://app.midtrans.com/snap/snap.js" 
      : "https://app.sandbox.midtrans.com/snap/snap.js";
      
    const clientKey = process.env.REACT_APP_MIDTRANS_CLIENT_KEY || "Mid-client-cxSG53OAPd_tPbD_";
    
    if (document.querySelector(`script[src="${src}"]`)) {
      return;
    }
    
    const script = document.createElement("script");
    script.src = src;
    script.setAttribute("data-client-key", clientKey);
    script.async = true;
    document.body.appendChild(script);
    
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const initiateMidtransPayment = useCallback(async (itemType, layerId) => {
    if (!state.parentId) return null;
    try {
      const data = await api.chargePayment({
        parent_id: state.parentId,
        item_type: itemType,
        layer_id: layerId ? Number(layerId) : null
      });
      return data; // { token: "...", redirect_url: "..." }
    } catch (err) {
      console.error("Failed to initiate Midtrans payment:", err);
      if (err.response?.status === 401) {
        toast.error(
          state.language === "id"
            ? "Sesi login Anda telah berakhir. Silakan keluar dan login kembali."
            : "Your login session has expired. Please logout and login again."
        );
      }
    }
    return null;
  }, [state.parentId, state.language]);

  const purchaseLayerSimulated = useCallback(async (layerId) => {
    if (!state.parentId) return false;
    try {
      const data = await api.checkoutSimulated({
        parent_id: state.parentId,
        item_type: "layer",
        layer_id: Number(layerId)
      });
      update({
        hasFullAccess: data.has_full_access,
        unlockedLayers: data.unlocked_layers || []
      });
      return true;
    } catch (err) {
      console.error("Failed to purchase layer:", err);
      if (err.response?.status === 401) {
        toast.error(
          state.language === "id"
            ? "Sesi login Anda telah berakhir. Silakan keluar dan login kembali."
            : "Your login session has expired. Please logout and login again."
        );
      }
    }
    return false;
  }, [state.parentId, update, state.language]);

  const purchaseFullAccessSimulated = useCallback(async () => {
    if (!state.parentId) return false;
    try {
      const data = await api.checkoutSimulated({
        parent_id: state.parentId,
        item_type: "full"
      });
      update({
        hasFullAccess: data.has_full_access,
        unlockedLayers: data.unlocked_layers || []
      });
      return true;
    } catch (err) {
      console.error("Failed to purchase full access:", err);
      if (err.response?.status === 401) {
        toast.error(
          state.language === "id"
            ? "Sesi login Anda telah berakhir. Silakan keluar dan login kembali."
            : "Your login session has expired. Please logout and login again."
        );
      }
    }
    return false;
  }, [state.parentId, update, state.language]);

  const addStars = useCallback((n = 1) => {
    dispatch(actions.addStars(n));
  }, [dispatch]);

  const toggleLanguage = useCallback(() => {
    dispatch(actions.toggleLanguage());
  }, [dispatch]);

  const toggleSound = useCallback(() => {
    dispatch(actions.toggleSound());
  }, [dispatch]);

  const toggleMusic = useCallback(() => {
    dispatch(actions.toggleMusic());
  }, [dispatch]);

  const toggleResearchMode = useCallback(() => {
    dispatch(actions.toggleResearchMode());
  }, [dispatch]);

  const togglePremium = useCallback(() => {
    dispatch(actions.togglePremium());
  }, [dispatch]);

  const logoutParent = useCallback(async () => {
    try {
      await api.logoutParent();
    } catch (err) {
      console.warn("Failed to notify backend of logout:", err);
    }
    dispatch(actions.clearState());
  }, [dispatch]);

  return (
    <GameContext.Provider
      value={{
        ...state,
        loaded,
        update,
        addStars,
        toggleLanguage,
        toggleSound,
        toggleMusic,
        toggleResearchMode,
        togglePremium,
        syncAccess,
        initiateMidtransPayment,
        purchaseLayerSimulated,
        purchaseFullAccessSimulated,
        logoutParent
      }}
    >
      {children}
    </GameContext.Provider>
  );
}

export function useGame() {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error("useGame must be used inside GameProvider");
  return ctx;
}
